import { ALL_CHECKLIST_TYPE, CHECKBOX_DEFAULT_KEY } from 'constants/ChecklistConstants';
import { COMPANY } from 'constants/Global';

import { TECHNICIAN } from 'constants/ModeConstants';
import { getObjectDiff } from 'utils/ObjectUtils';

import { isImported, cleanChecklists, getAdvices } from '../../../../services/ChecklistUtils';

/* *****************************************************************
 * This file will add "metadata" to the intervention entity
 * The "metadata" is used to "speed up" the form and "simplify" the code
 ***************************************************************** */

const CHECKLISTS_ORIGIN_PRIORITIES = {
  originTireReportId: 0,
  originTireScanId: 1,
  originBodyScanId: 2,
  originAuditId: 3,
  isProactive: Infinity,
};

/**
 * @param {import('modules/checklists/types/intervention-checklist').InterventionChecklist} checklist
 * @returns {string}
 */
const getSortKey = (checklist) => Object.keys(checklist).find(key => key in CHECKLISTS_ORIGIN_PRIORITIES);

/**
 * @param {import('modules/checklists/types/intervention-checklist').InterventionChecklist} checklistA
 * @param {import('modules/checklists/types/intervention-checklist').InterventionChecklist} checklistB
 * @returns {number}
 */
const sortChecklists = (checklistA, checklistB) => {
  const priorityA = CHECKLISTS_ORIGIN_PRIORITIES[getSortKey(checklistA)] ?? Infinity;
  const priorityB = CHECKLISTS_ORIGIN_PRIORITIES[getSortKey(checklistB)] ?? Infinity;

  return priorityA - priorityB;
};

/**
 * @param {Array<import('modules/checklists/types/intervention-checklist').InterventionChecklist>} checklists
 * @returns {Record<string, import('modules/checklists/types/intervention-checklist').InterventionChecklist>}
 */
export const splitChecklistsByType = (checklists = []) =>
  ALL_CHECKLIST_TYPE.reduce(
    (acc, type) => ({
      ...acc,
      [`checklists_${type}`]: checklists
        .filter(({ type: checklistType }) => checklistType === type)
        .sort(sortChecklists)
    }),
    {}
  );

/**
 * @param {import('modules/checklists/types/intervention-checklist').InterventionChecklist} checklist
 * @param {import('modules/checklists/types/resources/advices/intervention-checklist-advice').InterventionChecklistAdvice} advice
 * @param {import('modules/global/typing/enums/modes.enum').ModeEnum} mode
 * @returns {import('modules/checklists/types/resources/advices/intervention-checklist-advice').EnrichedInterventionChecklistAdvice}
 */
const enrichChecklistAdvice = (checklist, advice, mode) => ({
  ...advice,
  fromImport: isImported(checklist, mode),
  fromType: checklist.type,
  payment: checklist.payment,
});

/**
 * @typedef {{
 *  mode?: import('modules/global/typing/enums/modes.enum').ModeEnum,
 *  severityFilter?: Array<import("@arhs-fleetback/types/entities/checklists").ChecklistSeverity>,
 *  statusFilter?: Array<import("@arhs-fleetback/types/entities/checklists").ChecklistAdviceStatus>,
 * }} FilterParams
 */

/**
 * @param {import('modules/checklists/types/resources/items/intervention-checklist-item').InterventionChecklistItem} item
 * @param {import('modules/checklists/types/intervention-checklist').InterventionChecklist} checklist
 * @param {FilterParams} params
 * @returns {import('modules/checklists/types/resources/items/intervention-checklist-item').EnrichedInterventionChecklistItem}
 */
const enrichChecklistItem = (item, checklist, { severityFilter = [], statusFilter = [], mode = TECHNICIAN }) => ({
  ...item,
  fromImport: isImported(checklist, mode),
  fromType: checklist.type,
  payment: checklist.payment,
  advices: (item.advices ?? [])
    .filter(advice =>
      (severityFilter.length === 0 || severityFilter.includes(advice.severity)) &&
      (statusFilter.length === 0 || statusFilter.includes(advice.status)))
    .map(advice => enrichChecklistAdvice(checklist, advice, mode)),
});

/**
 * @param {import('modules/checklists/types/intervention-checklist').InterventionChecklist} checklist
 * @param {FilterParams} params
 * @returns {import('modules/checklists/types/intervention-checklist').EnrichedInterventionChecklist}
 */
const enrichChecklist = (checklist, params) => ({
  ...checklist,
  fromImport: isImported(checklist, params.mode),
  items: checklist.items?.map(item => enrichChecklistItem(item, checklist, params)) ?? [],
});

/**
 * @param {Array<import('modules/checklists/types/intervention-checklist').InterventionChecklist>} checklists
 * @param {FilterParams} params
 * @returns {Array<import('modules/checklists/types/intervention-checklist').EnrichedInterventionChecklist>}
 */
export const enrichChecklists = (checklists = [], params = {}) =>
  cleanChecklists(checklists)
    .sort(({ default: defaultA = false }, { default: defaultB = false }) => {
      if (defaultA === defaultB) {
        return 0;
      }
      return defaultA ? -1 : 1;
    })
    .map(checklist => enrichChecklist(checklist, params));

/**
 * @param {import('modules/interventions/types/projections/detailed-intervention').DetailedTireReport} tireReport
 * @param {import("modules/interventions/types/projections/detailed-intervention").TirePositionEnum[]} tirePosition
 * @returns {import('modules/interventions/types/projections/detailed-intervention').DetailedTire[]}
 */
const getTireReportInitialValue = (tireReport, tirePosition) =>
  tirePosition.map(position => ({ position, ...tireReport?.tires?.find(tire => tire.position === position) }));

/**
 * @param {Partial<import('modules/interventions/types/projections/detailed-intervention').DetailedIntervention>} intervention
 * @param {{
 *   severityFilter: Array<import("@arhs-fleetback/types/entities/checklists").ChecklistSeverity>,
 *   tirePosition?: import("modules/interventions/types/projections/detailed-intervention").TirePositionEnum[],
 * }} options
 * @returns {import('modules/interventions/types/projections/form-detailed-intervention').FormDetailedIntervention}
 */
export const enrichIntervention = (intervention = {}, options = {}) => {
  const { checklists, mode, brand } = intervention;
  const { severityFilter = [], tirePosition = [] } = options;

  return {
    ...intervention,
    ...splitChecklistsByType(enrichChecklists(
      checklists,
      { severityFilter, statusFilter: [], mode }
    )),
    pristine: true,
    brandId: brand?.id,
    hasImported: false,
    tireReport: { modifiedOn: intervention?.tireReport?.modifiedOn, tires: getTireReportInitialValue(intervention.tireReport, tirePosition) }
  };
};

export const concatChecklists = data => ALL_CHECKLIST_TYPE.flatMap(type => data[`checklists_${type}`] ?? []);

export const removeChecklistsByType = enriched =>
  ALL_CHECKLIST_TYPE.reduce((cleanedIntervention, type) => {
    const field = `checklists_${type}`;
    const { [field]: _omitted, ...intervention } = cleanedIntervention;
    return intervention;
  }, enriched);

const unenrichChecklistAdvice = ({ fromImport: _, fromType: __, payment: ___, ...advice }) => ({
  ...advice,
  discount: advice.discount || 0,
});

const unenrichChecklistItem = ({ fromImport: _, fromType: __, payment: ___, ...item }) => ({
  ...item,
  advices: item.advices?.map(unenrichChecklistAdvice) ?? [],
});

const unenrichChecklist = ({ fromImport: _, ...checklist }) => ({
  ...checklist,
  items: checklist.items?.map(unenrichChecklistItem) ?? [],
});

const cleanCustomerInfo = customerInfo => {
  if (customerInfo?.phoneNumber) {
    const { error: _, isLoading: __, ...phoneNumber } = customerInfo.phoneNumber;
    return {
      ...customerInfo,
      phoneNumber,
    };
  }
  return customerInfo;
};

export const unenrichIntervention = ({ brandId: _, pristine: __, hasImported: ___, ...intervention }) => ({
  ...removeChecklistsByType(intervention),
  customerInfo: cleanCustomerInfo(intervention.customerInfo),
  checklists: concatChecklists(intervention).map(unenrichChecklist),
});

export const formatCustomerInfo = customerInfo => {
  if (customerInfo && customerInfo.civility) {
    // eslint-disable-next-line prefer-const
    let { companyName: _, ...formattedCustomerInfo } = customerInfo;

    if (customerInfo.civility === COMPANY) {
      const { firstName: __, lastName: ___, ...rest } = customerInfo;
      formattedCustomerInfo = rest;
    }

    return formattedCustomerInfo;
  }

  return customerInfo;
};

/**
 *
 * @param {Array<{ name?: string, id: string }>} checklistGroups
 * @param {import('react-intl').IntlShape} intl
 * @returns {?Array<{name: string, id: string}>}
 */
export const formatChecklistGroup = (checklistGroups, intl) =>
  checklistGroups?.map(checklistGroup => ({
    ...checklistGroup,
    name:
      checklistGroup.name ??
      intl.formatMessage({
        id: 'page.fiche.form.pending.checklistGroup.defaultName',
        defaultMessage: 'Vehicle 1',
      }),
  }));

/**
   * @param {import('modules/interventions/types/projections/detailed-intervention').DetailedTireReport} tireReport
   */
export const formatTireReport = (tireReport) => {
  const isDefined = tireReport?.id ||
    tireReport?.tires?.some(({ position: _, ...fields }) => Object.values(fields).some(v => v !== null && v !== undefined));
  return isDefined ? tireReport : undefined;
};

/**
 * @param {Array<import("modules/checklists/types/projections/detailed-checklist").DetailedChecklist>} checklists
 * @param {string} key
 * @returns {Array<import("modules/checklists/types/working/checklist-advice-form").ChecklistAdviceForm>}
 */
export const getCheckedAdvicesByKey = (checklists, advicesFormatter, key = CHECKBOX_DEFAULT_KEY) =>
  getAdvices(checklists ?? [], undefined, advicesFormatter)
    .filter((advice) => Boolean(advice[key]));

const SKIPPED_ENTRIES = ['conversationContext.channels', 'serviceAdvisorUser', 'assignedUser'];

export const initialValuesEqual = (formValueA, formValueB) =>
  getObjectDiff(formValueA, formValueB).every(entry => SKIPPED_ENTRIES.some(skipped => entry.startsWith(skipped)));
